import type { PrescriptionLayoutResponse } from '@doccareplus/ui/prescription';
import { BaseApiService } from '@doccareplus/api-client';

// HTTP adapter for the patient-portal records endpoints (api/portal/records/*). Auth is by
// PatientPortalLink ownership — the requesting user + active link are resolved server-side from the JWT and
// the X-Portal-Link-Id header (applied globally, see profiles' `applyActiveProfileHeader`), never sent
// per-request here. All JSON is camelCase; these types mirror the C# Contracts.Portal.Records records 1:1.

/** Mirrors Contracts.Portal.Records.PortalPrescriptionSummaryDto. */
export interface PortalPrescriptionSummaryDto {
  visitId: string;
  visitDate: string;
  finalizedAt: string;
  providerDisplayName: string;
  medicinesPreview: string | null;
}

/** Mirrors Contracts.Portal.Records.PortalPrescriptionItemDto. */
export interface PortalPrescriptionItemDto {
  medicineName: string;
  dose: string | null;
  frequency: string | null;
  duration: string | null;
  instructions: string | null;
  order: number;
}

/** Mirrors Contracts.Portal.Records.PortalPrescriptionDto. */
export interface PortalPrescriptionDto {
  visitId: string;
  patientDisplayName: string;
  clinicDisplayName: string;
  providerDisplayName: string;
  signedByDisplayName: string | null;
  visitDate: string;
  finalizedAt: string;
  diagnosis: string | null;
  patientAdvice: string | null;
  followUpDate: string | null;
  items: PortalPrescriptionItemDto[];
  investigations: string[];
  /**
   * The doctor half of the letterhead, frozen at signing. Absent when the provider never recorded it — the
   * letterhead omits the line rather than substituting placeholder text.
   */
  providerQualification?: string | null;
  providerRegistrationNumber?: string | null;
  patientNumber?: string | null;
  patientAge?: number | null;
  patientGender?: string | null;
  chiefComplaint?: string | null;
  allergySummary?: string | null;
  vitals?: {
    weight?: number | null;
    weightUnit?: string | null;
    systolic?: number | null;
    diastolic?: number | null;
    temperature?: number | null;
    temperatureUnit?: string | null;
    pulse?: number | null;
  } | null;
  /**
   * The issuing clinic's live letterhead and effective prescription format, so a patient's copy identifies and can
   * contact the clinic and reads in its system of medicine. The clinic's print visibility toggles are already
   * applied server-side, so a field that arrives absent must not be rendered.
   */
  layout?: PrescriptionLayoutResponse | null;
}

export interface MyPrescriptionsResponse {
  prescriptions: PortalPrescriptionSummaryDto[];
}

class RecordsApiService extends BaseApiService {
  constructor() {
    super('/portal');
  }

  listPrescriptions(): Promise<MyPrescriptionsResponse> {
    return this.get<MyPrescriptionsResponse>('/records/prescriptions');
  }

  getPrescription(visitId: string): Promise<PortalPrescriptionDto> {
    return this.get<PortalPrescriptionDto>(`/records/prescriptions/${visitId}`);
  }
}

export const recordsApi = new RecordsApiService();
